import { Stack, styled, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IntermediateProductAsIngredient, ProductIngredient } from "src/api/client-api/products/useGetOneProduct";
import { URLHandler } from "src/services/URLHandler";
import { FoodsIcon, IconMapping, LinearProgress } from "@foodpilot/foods";
import { NavLink } from "react-router";
import { useProductScoresContext } from "src/context/Product/Scores/useProductScores";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { IngredientTooltip } from "./IngredientTooltip";
import { canOpenLineItem } from "src/pages/Product/lineItemAccess";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import {
    getIngredientQuestionnaireEntryUrl,
    getQuestionnaireSupplierId,
} from "src/pages/Ingredient/ingredientQuestionnaireAccess";

type ProductIngredientDataRowProps = {
    productIngredient: ProductIngredient;
    unit: string;
};

const StyledNavLink = styled(NavLink)(({ theme }) => ({
    color: theme.palette.text.primary,
    textDecoration: "none",
    padding: 0,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
}));

export const ProductIngredientDataRow = (props: ProductIngredientDataRowProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { productIngredient, unit } = props;
    const { company } = useCompanyMetadata();
    const { isUserOfTypeSupplier } = useFoodpilotContext();
    const { productSummary } = useProductScoresContext();

    const isIntermediateProduct = hasIntermediateProduct(productIngredient);

    const entityId = isIntermediateProduct ? productIngredient.intermediateProduct.id : productIngredient.ingredient.id;
    const entityName =
        isIntermediateProduct ? productIngredient.intermediateProduct.title : productIngredient.ingredient.name;

    const ssqUrl =
        isIntermediateProduct ?
            URLHandler.intermediateProduct.ssq(company.id, productIngredient.intermediateProduct.id)
        :   getIngredientQuestionnaireEntryUrl({
                companyId: company.id,
                ingredientId: productIngredient.ingredient.id,
                questionnaireSupplierId: getQuestionnaireSupplierId({
                    selectedCompanyId: company.id,
                    isSupplierUser: isUserOfTypeSupplier(),
                    suppliers: productIngredient.ingredient.suppliers,
                }),
            });

    const ingredientSuppliersCount = !isIntermediateProduct ? (productIngredient.ingredient.suppliers ?? []).length : 0;

    const entityProductSummary = !isIntermediateProduct ? productSummary?.ingredients : undefined;
    const summaryCompletions = entityProductSummary?.completions.byEntityId[entityId];
    const summaryMetadata = entityProductSummary?.metadata[entityId];

    const icon: keyof IconMapping = summaryCompletions?.weightedAverage === 100 ? "circleCheck" : "inProgress";

    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Stack flex={3}>
                <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                    {isIntermediateProduct && <FoodsIcon icon="products" size={2} />}
                    <Typography variant="h4">{entityName}</Typography>
                </Stack>
                {summaryMetadata?.hasProgress && (
                    <Stack direction="row" alignItems="center" gap={1} color={theme.palette.text.secondary}>
                        <FoodsIcon icon={icon} size={2} />
                        <Typography variant="small-bold" color={theme.palette.text.secondary}>
                            {summaryCompletions?.completedCount} / {ingredientSuppliersCount}{" "}
                            {t("ingredient.activity_form.suppliers", { count: ingredientSuppliersCount })}
                        </Typography>
                    </Stack>
                )}
            </Stack>

            <Stack flex={2} justifyContent="flex-start">
                {summaryMetadata?.hasProgress === false ?
                    <IngredientTooltip
                        noReference={summaryMetadata?.reference === undefined}
                        noScoreReference={summaryMetadata?.reference?.score === undefined}
                        content={{
                            label: summaryMetadata?.reference?.label,
                            value: summaryMetadata?.reference?.score,
                            ssqUrl,
                            unit,
                        }}
                    />
                : summaryCompletions ?
                    <LinearProgress value={summaryCompletions.weightedAverage} />
                :   "-"}
            </Stack>

            <Stack flex={1} alignItems="flex-end">
                {(isIntermediateProduct || canOpenLineItem(productIngredient.ingredient)) && (
                    <StyledNavLink to={ssqUrl} target="_blank">
                        <FoodsIcon icon="openNewTab" size={3} />
                    </StyledNavLink>
                )}
            </Stack>
        </Stack>
    );
};

const hasIntermediateProduct = (
    entity: ProductIngredient,
): entity is ProductIngredient & { intermediateProduct: IntermediateProductAsIngredient } => {
    return entity.intermediateProduct !== undefined;
};
